import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Check, X, WarningCircle } from "@phosphor-icons/react";

const RED = "#E3000B", GREEN = "#00A850", YELLOW = "#FFD500";

function beep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "square"; o.frequency.value = ok ? 1320 : 220; g.gain.value = 0.08;
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + (ok ? 0.12 : 0.3));
    if (navigator.vibrate) navigator.vibrate(ok ? 40 : [80, 40, 80]);
  } catch { /* audio blocked — ignore */ }
}

/** Continuous camera scanner: each new UPC/EAN → onCode(code) → {found,set_number,name}.
 *  Full, un-cropped camera frame · narrow barcode window · big green/red confirmation · running list. */
export function BarcodeScanner({ onCode, onClose, count }) {
  const videoRef = useRef(null);
  const seen = useRef(new Set());
  const busy = useRef(false);
  const [error, setError] = useState("");
  const [last, setLast] = useState(null);   // {code, ok, label}
  const [flash, setFlash] = useState(null); // "ok" | "bad" — brief full-screen tint
  const [added, setAdded] = useState([]);   // [{set_number, name}] newest first

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.EAN_13, BarcodeFormat.EAN_8]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 60, delayBetweenScanSuccess: 700 });
    let controls;
    (async () => {
      try {
        const constraints = { audio: false, video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 }, advanced: [{ focusMode: "continuous" }] } };
        controls = await reader.decodeFromConstraints(constraints, videoRef.current, async (result) => {
          if (!result || busy.current) return;
          const code = result.getText().replace(/\D/g, "");
          if (!code || seen.current.has(code)) return;
          seen.current.add(code);
          busy.current = true;
          if (navigator.vibrate) navigator.vibrate(20);
          setLast({ code, ok: null, label: "Barcode read — looking up the set…" });
          try {
            const r = await onCode(code);
            beep(!!r?.found);
            setFlash(r?.found ? "ok" : "bad"); setTimeout(() => setFlash(null), 450);
            if (r?.found) {
              setAdded(a => [{ set_number: r.set_number, name: r.name || "" }, ...a].slice(0, 6));
              setLast({ code, ok: true, label: `#${r.set_number.replace(/-1$/, "")}`, sub: r.name || "" });
            } else {
              setLast({ code, ok: false, label: "Not recognised", sub: "Type the set number instead" });
              setTimeout(() => seen.current.delete(code), 4000);
            }
          } catch {
            beep(false); setFlash("bad"); setTimeout(() => setFlash(null), 450);
            setLast({ code, ok: false, label: "Lookup failed", sub: "Try again" });
            seen.current.delete(code);
          }
          busy.current = false;
        });
      } catch (e) {
        setError(/NotAllowed|Permission/i.test(String(e?.name || e)) ? "Camera permission was denied. Allow camera access and try again." : "Couldn't start the camera on this device.");
      }
    })();
    return () => { try { controls?.stop(); } catch { /* noop */ } };
  }, [onCode]);

  const total = count || 0;
  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col select-none" data-testid="o4b-scanner">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <div className="font-black tracking-wide text-base">Scan the barcode on the box</div>
          <div className="text-[11px] text-white/60 font-mono">UPC is on the bottom or side of the box</div>
        </div>
        <button onClick={onClose} aria-label="Close scanner" className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center active:scale-95" data-testid="o4b-scanner-close">
          <X size={22} weight="bold" />
        </button>
      </div>

      <div className="relative flex-1 min-h-0 bg-black">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-contain" muted playsInline />
        {flash && <div className="absolute inset-0 pointer-events-none" style={{ background: flash === "ok" ? GREEN : RED, opacity: 0.55 }} data-testid="o4b-scanner-flash" />}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative w-[72%] max-w-[340px] h-24 rounded-xl border-[3px]" style={{ borderColor: last?.ok === false ? RED : last?.ok ? GREEN : YELLOW, boxShadow: "0 0 0 9999px rgba(0,0,0,.35)" }}>
            <div className="absolute left-2 right-2 top-1/2 h-[2px] -translate-y-1/2 scan-laser" style={{ background: RED }} />
          </div>
        </div>
        {error && (
          <div className="absolute inset-x-5 top-1/2 -translate-y-1/2 rounded-2xl bg-white text-black p-4 text-center font-bold" data-testid="o4b-scanner-error">{error}</div>
        )}
        {last && (
          <div className="absolute inset-x-4 bottom-4 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl" style={{ background: last.ok === false ? RED : last.ok ? GREEN : "#222" }} data-testid="o4b-scanner-status">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              {last.ok ? <Check size={24} weight="bold" /> : last.ok === false ? <WarningCircle size={24} weight="bold" /> : <span className="w-5 h-5 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-mono opacity-80">{last.ok ? "ADDED" : last.ok === false ? "NOT ADDED" : "READING"} · {last.code}</div>
              <div className="font-black text-xl leading-tight truncate">{last.label}</div>
              {last.sub && <div className="text-sm font-semibold truncate opacity-90">{last.sub}</div>}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-5 space-y-3 bg-black">
        {added.length > 0 ? (
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="o4b-scanner-added">
            {added.map((a, i) => (
              <div key={`${a.set_number}-${i}`} className="shrink-0 rounded-xl px-3 py-2 bg-white/10 border border-white/15 min-w-[120px]" data-testid={`o4b-scanner-added-${a.set_number}`}>
                <div className="font-mono text-xs font-black" style={{ color: YELLOW }}>#{a.set_number.replace(/-1$/, "")}</div>
                <div className="text-[11px] font-semibold truncate max-w-[150px]">{a.name}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-white/70 text-center">Line the barcode up inside the yellow box · it scans automatically</div>
        )}
        <button onClick={onClose} className="w-full h-14 rounded-2xl font-black text-lg active:scale-[.99]" style={{ background: total ? GREEN : "#fff", color: total ? "#fff" : "#000" }} data-testid="o4b-scanner-done">
          {total ? `Done — ${total} set${total === 1 ? "" : "s"} added` : "Done"}
        </button>
      </div>
    </div>
  );
}
